// Tax Center → FMV & prizes: fair market value and payment category per
// ticket tier, FMV per auction lot, prize details per raffle. A blank FMV on
// a nonprofit is flagged.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingState from '../common/LoadingState';
import { getFmv, saveTierFmv, saveAuctionFmv, saveRafflePrize, errorText, money, toCents } from '../../services/taxApi';

const dollars = (c) => (c == null ? '' : (c / 100).toFixed(2));

function useRowSave() {
  const [state, setState] = useState({});
  const run = async (key, fn) => {
    setState((s) => ({ ...s, [key]: { busy: true } }));
    try { await fn(); setState((s) => ({ ...s, [key]: { ok: true } })); } catch (e) { setState((s) => ({ ...s, [key]: { err: errorText(e, 'Could not save.') } })); }
  };
  return [state, run];
}

function TierRow({ tier, nonprofit, save, st, t }) {
  const [fmv, setFmv] = useState(dollars(tier.fmvCents));
  const [category, setCategory] = useState(tier.category || 'purchase');
  const blank = nonprofit && fmv === '';
  return (
    <tr>
      <td>{tier.event || '—'}</td>
      <td>{tier.name}</td>
      <td className="tx-num">{money(tier.priceCents)}</td>
      <td className="tx-num">{tier.ordersThisYear}</td>
      <td>
        <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label={t('tax.fmv.category', 'Category')}>
          <option value="purchase">{t('tax.fmv.purchase', 'Purchase')}</option>
          <option value="donation">{t('tax.fmv.donation', 'Donation')}</option>
        </select>
      </td>
      <td>
        <input type="text" inputMode="decimal" value={fmv} onChange={(e) => setFmv(e.target.value)} placeholder={t('tax.fmv.notSet', 'Not set')} aria-label={t('tax.fmv.fmv', 'FMV')} />
        {blank && <span className="tx-err">{t('tax.fmv.blankWarn', 'FMV not set')}</span>}
      </td>
      <td>
        <button type="button" className="lm-btn secondary" disabled={st && st.busy} onClick={() => {
          const c = toCents(fmv);
          save(`t${tier.id}`, async () => {
            if (Number.isNaN(c)) throw new Error(t('tax.badAmount', 'Enter an amount like 1250.00'));
            await saveTierFmv(tier.id, { fmvCents: c, category });
          });
        }}>{t('tax.save', 'Save')}</button>
        {st && st.ok && <span className="tx-saved"> {t('tax.saved', 'Saved.')}</span>}
        {st && st.err && <span className="tx-err"> {st.err}</span>}
      </td>
    </tr>
  );
}

function LotRow({ lot, nonprofit, save, st, t }) {
  const [fmv, setFmv] = useState(dollars(lot.fmvCents));
  return (
    <tr>
      <td>{lot.event || '—'}</td>
      <td>{lot.name || '—'}</td>
      <td>
        <input type="text" inputMode="decimal" value={fmv} onChange={(e) => setFmv(e.target.value)} placeholder={t('tax.fmv.notSet', 'Not set')} aria-label={t('tax.fmv.fmv', 'FMV')} />
        {nonprofit && fmv === '' && <span className="tx-err">{t('tax.fmv.blankWarn', 'FMV not set')}</span>}
      </td>
      <td>
        <button type="button" className="lm-btn secondary" disabled={st && st.busy} onClick={() => {
          const c = toCents(fmv);
          save(`a${lot.id}`, async () => {
            if (Number.isNaN(c)) throw new Error(t('tax.badAmount', 'Enter an amount like 1250.00'));
            await saveAuctionFmv(lot.id, c);
          });
        }}>{t('tax.save', 'Save')}</button>
        {st && st.ok && <span className="tx-saved"> {t('tax.saved', 'Saved.')}</span>}
        {st && st.err && <span className="tx-err"> {st.err}</span>}
      </td>
    </tr>
  );
}

function RaffleRow({ raffle, save, st, t }) {
  const [p, setP] = useState({ description: raffle.prizeDescription || '', value: dollars(raffle.prizeValueCents), cash: raffle.prizeIsCash == null ? '' : (raffle.prizeIsCash ? 'cash' : 'noncash') });
  return (
    <tr>
      <td>{raffle.event || '—'}</td>
      <td>{raffle.name}</td>
      <td><input type="text" value={p.description} onChange={(e) => setP({ ...p, description: e.target.value })} aria-label={t('tax.winners.prize', 'Prize')} /></td>
      <td><input type="text" inputMode="decimal" value={p.value} onChange={(e) => setP({ ...p, value: e.target.value })} placeholder={t('tax.fmv.notSet', 'Not set')} aria-label={t('tax.winners.value', 'Value (cash or FMV)')} /></td>
      <td>
        <select value={p.cash} onChange={(e) => setP({ ...p, cash: e.target.value })} aria-label={t('tax.winners.type', 'Prize type')}>
          <option value="">{t('tax.fmv.notSet', 'Not set')}</option>
          <option value="cash">{t('tax.winners.cash', 'Cash')}</option>
          <option value="noncash">{t('tax.winners.noncash', 'Non-cash (FMV)')}</option>
        </select>
      </td>
      <td>
        <button type="button" className="lm-btn secondary" disabled={st && st.busy} onClick={() => {
          const c = toCents(p.value);
          save(`r${raffle.id}`, async () => {
            if (Number.isNaN(c)) throw new Error(t('tax.badAmount', 'Enter an amount like 1250.00'));
            await saveRafflePrize(raffle.id, { prizeDescription: p.description, prizeValueCents: c, prizeIsCash: p.cash === '' ? null : p.cash === 'cash' });
          });
        }}>{t('tax.save', 'Save')}</button>
        {st && st.ok && <span className="tx-saved"> {t('tax.saved', 'Saved.')}</span>}
        {st && st.err && <span className="tx-err"> {st.err}</span>}
      </td>
    </tr>
  );
}

export default function TaxFmv({ year }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [rowState, save] = useRowSave();

  const load = useCallback(async () => {
    setError('');
    try { setData(await getFmv(year)); } catch (err) { setError(errorText(err, t('tax.fmv.loadError', 'Could not load fair market values.'))); }
  }, [year, t]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="note warn">{error}</div>;
  if (!data) return <LoadingState />;
  const np = data.nonprofit;

  return (
    <>
      <section className="surface" aria-labelledby="tx-fmv-tiers">
        <h4 id="tx-fmv-tiers">{t('tax.fmv.tiersTitle', 'Ticket tiers')}</h4>
        <p className="surface-muted perf-small">{t('tax.fmv.tiersIntro', 'Fair market value (FMV) is what the goods or services a buyer receives are worth — dinner, entertainment, merchandise. The deductible part of a payment is what was paid minus FMV. Mark a tier “Donation” when it is a gift; leave FMV at 0 if the donor receives nothing.')}</p>
        {data.tiers.length === 0 ? <p className="surface-muted perf-small">{t('tax.fmv.noTiers', 'No paid ticket tiers.')}</p> : (
          <div className="perf-table-wrap">
            <table className="perf-table tx-table">
              <thead><tr>
                <th>{t('tax.fmv.event', 'Event')}</th><th>{t('tax.fmv.tier', 'Tier')}</th><th className="tx-num">{t('tax.fmv.price', 'Price')}</th>
                <th className="tx-num">{t('tax.fmv.orders', 'Orders {{year}}', { year })}</th><th>{t('tax.fmv.category', 'Category')}</th><th>{t('tax.fmv.fmv', 'FMV')}</th><th />
              </tr></thead>
              <tbody>{data.tiers.map((tier) => <TierRow key={tier.id} tier={tier} nonprofit={np} save={save} st={rowState[`t${tier.id}`]} t={t} />)}</tbody>
            </table>
          </div>
        )}
      </section>

      <section className="surface" aria-labelledby="tx-fmv-lots">
        <h4 id="tx-fmv-lots">{t('tax.fmv.lotsTitle', 'Auction lots')}</h4>
        {data.auctions.length === 0 ? <p className="surface-muted perf-small">{t('tax.fmv.noLots', 'No auctions.')}</p> : (
          <div className="perf-table-wrap">
            <table className="perf-table tx-table">
              <thead><tr><th>{t('tax.fmv.event', 'Event')}</th><th>{t('tax.fmv.lot', 'Lot')}</th><th>{t('tax.fmv.fmv', 'FMV')}</th><th /></tr></thead>
              <tbody>{data.auctions.map((lot) => <LotRow key={lot.id} lot={lot} nonprofit={np} save={save} st={rowState[`a${lot.id}`]} t={t} />)}</tbody>
            </table>
          </div>
        )}
      </section>

      <section className="surface" aria-labelledby="tx-fmv-raffles">
        <h4 id="tx-fmv-raffles">{t('tax.fmv.rafflesTitle', 'Raffle prizes')}</h4>
        <p className="surface-muted perf-small">{t('tax.fmv.rafflesIntro', 'The prize value decides whether a winner needs Form W-2G. Non-cash prizes use fair market value. Individual winners can be overridden on the Raffle winners tab.')}</p>
        {data.raffles.length === 0 ? <p className="surface-muted perf-small">{t('tax.fmv.noRaffles', 'No raffles.')}</p> : (
          <div className="perf-table-wrap">
            <table className="perf-table tx-table">
              <thead><tr>
                <th>{t('tax.fmv.event', 'Event')}</th><th>{t('tax.fmv.raffle', 'Raffle')}</th><th>{t('tax.winners.prize', 'Prize')}</th>
                <th>{t('tax.winners.value', 'Value (cash or FMV)')}</th><th>{t('tax.winners.type', 'Prize type')}</th><th />
              </tr></thead>
              <tbody>{data.raffles.map((r) => <RaffleRow key={r.id} raffle={r} save={save} st={rowState[`r${r.id}`]} t={t} />)}</tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
